import { unifiedApi } from "@/common";
import { IAllstateState, ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { LAST_API_FETCH_INTERVAL } from "@/define";
import { logEvent } from "@/common/logger";
import { defineStore } from "pinia";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { computed, ref } from "vue";
import { pageStates } from "@/components/Allstate/AllstateEnums";

export const useAllstateStore = defineStore("allstate", () => {
  const allStateInfo = ref<IAllstateState>({
    allstateSubscriptions: [],
    lastRequestTime: 0,
    isRunning: false,
    hasAccountSetup: false,
  });
  const pageState = ref<pageStates>(pageStates.notPurchased);
  const keycode = ref<string>("");
  const subscriptionName = ref<string>("");
  const subscriptionSku = ref<string>("");
  const isExpired = ref<boolean>(false);
  const isExpiring = ref<boolean>(false);
  const isInactive = ref<boolean>(false);
  const justSet = ref<boolean>(false);
  const activeSubscription = ref<ISubscription>();
  const allstateSubscriptions = computed(() => allStateInfo.value.allstateSubscriptions || []);

  const loading = ref<boolean>(true);

  const hasEnrolledAllstateSubscription = computed(() => allstateSubscriptions.value.some(sub => !sub.canceledOnDate));

  async function populateAllStateInfo() {
    try {
      // Waits for the current call to finish before trying and get aborted (in case a simultaneous call)
      while (allStateInfo.value.isRunning) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const currentTime = new Date().getTime();
      if (currentTime - allStateInfo.value.lastRequestTime >= LAST_API_FETCH_INTERVAL) {
        allStateInfo.value.lastRequestTime = new Date().getTime();
        allStateInfo.value.isRunning = true;
        allStateInfo.value.allstateSubscriptions = (await unifiedApi.getAllstateSubscriptions()).data;
        allStateInfo.value.hasAccountSetup =
          allStateInfo.value.allstateSubscriptions &&
          allStateInfo.value.allstateSubscriptions.some(a => a.canceledOnDate === null);
        allStateInfo.value.isRunning = false;
        logEvent("allstateSubscriptions state updated", "subscriptions store", allStateInfo.value);
      }
    } catch (err) {
      //redirect to error page
      handleApiError(err as AxiosError, false);
      // return existing state to satisfy places it is called even though it will be redirected
      return allStateInfo.value;
    }
  }

  async function getAllstateSubscriptions() {
    try {
      // Waits for the current call to finish before trying and get aborted (in case a simultaneous call)
      while (allStateInfo.value.isRunning) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const currentTime = new Date().getTime();
      if (currentTime - allStateInfo.value.lastRequestTime >= LAST_API_FETCH_INTERVAL) {
        allStateInfo.value.lastRequestTime = new Date().getTime();
        allStateInfo.value.isRunning = true;
        allStateInfo.value.allstateSubscriptions = (await unifiedApi.getAllstateSubscriptions()).data;
        allStateInfo.value.hasAccountSetup =
          allStateInfo.value.allstateSubscriptions &&
          allStateInfo.value.allstateSubscriptions.some(a => a.canceledOnDate === null);
        allStateInfo.value.isRunning = false;
        logEvent("allstateSubscriptions state updated", "subscriptions store", allStateInfo.value);
        return allStateInfo.value;
      }
      return allStateInfo.value;
    } catch (err) {
      //redirect to error page
      handleApiError(err as AxiosError, false);
      // return existing state to satisfy places it is called even though it will be redirected
      return allStateInfo.value;
    }
  }

  //If the user has updated their subscriptions off site we want to refresh the store next time they come to the page
  function forceRefresh() {
    allStateInfo.value.lastRequestTime = new Date().getTime() - LAST_API_FETCH_INTERVAL;
    logEvent("Force AllstateSubscriptions store refresh");
  }

  async function forceRefreshAndGetAllstateSubscriptions() {
    forceRefresh();
    return await getAllstateSubscriptions();
  }

  return {
    allStateInfo,
    pageState,
    keycode,
    subscriptionName,
    subscriptionSku,
    isExpired,
    isExpiring,
    isInactive,
    justSet,
    activeSubscription,
    allstateSubscriptions,
    loading,
    hasEnrolledAllstateSubscription,
    getAllstateSubscriptions,
    forceRefresh,
    forceRefreshAndGetAllstateSubscriptions,
    populateAllStateInfo,
  };
});
